import { applyTransaction, arrayRemove, arrayUpdate, combineQueries, type EntityStore, Order, resetStores } from '@datorama/akita';
import { type GridGetRowsParams } from '@mui/x-data-grid-premium';
import * as Sentry from '@sentry/react';
import { formatISO } from 'date-fns';
import { isEqual, pick } from 'lodash-es';
import { type Layout, type LayoutItem } from 'react-grid-layout/legacy';
import { debounceTime, distinctUntilChanged, firstValueFrom, map, tap } from 'rxjs';
import ability from 'casl/ability';
import { layoutPropsToSave } from 'components/Chart/layoutConfig';
import { pageSize$, userContextQuery } from 'context/UserContext/query';
import userContextStore from 'context/UserContext/store';
import { businessActivitiesStore } from 'state/BusinessActivities/store';
import CRUDService from 'state/CRUDService';
import customFiltersStore from 'state/CustomUserFilters/store';
import footprintsStore from 'state/Footprints/store';
import orgStore from 'state/Organisations/store';
import searchHistoryStore from 'state/Search/store';
import taskStore from 'state/Task/store';
import uiStore from 'state/UI/store';
import { applyUserListFilters } from 'state/User/listQuery';
import { query } from 'state/User/query';
import store, { type UsersState } from 'state/User/store';
import { type Notification, type Organisation, type Period, type User } from 'types/models';
import baseClient from 'utils/axiosBaseClient';
import { type AuditLogEventEnum } from 'utils/enum';
import { type ExtractObservableValueType } from 'utils/modules/state';

const persistableViewStateProperties = [
   'activeDashboardTab',
   'organisationsView',
   'organisationsViewTopbar',
   'mainNavigationOpen',
   'keyFigureReportReportingStandardIds',
   'keyFigureTagIds',
   'reportGroupByOrganisation',
   'reportShowEmptyFields',
   'reportOnlyApproved',
   'statisticsFilters',
   'KPITableFieldInGridMode',
   'reportEditorMergeFieldsPreviewMode',
   'taxonomyDisclosureCutOffDate',
   'orgaTreeSortOrder',
   'strategyModuleViewState',
   'generalRiskModuleMitigationsViewState',
   'showOutOfScopeEmissionItems',
   'organisationsViewOrder',
];

export default class UsersService extends CRUDService<UsersState> {
   private static instance: UsersService;
   private contextStore = userContextStore;
   private baseClient = baseClient;
   private hasLoaded = false;
   private userPromise?: Promise<any> = undefined;

   constructor() {
      if (!UsersService.instance) {
         super('users', store, query, {
            useOrganisationContext: true,
            useScope: false,
            includeSubsidiaries: true,
            paginate: true,
         });

         UsersService.instance = this;
      }

      // UsersService shall be instantiated only once, because otherwise the observable will be created for each service instance
      return UsersService.instance;
   }

   logout = async () => {
      try {
         await this.persistViewState();
         this.resetHasLoaded();

         await this.baseClient.post('/auth/logout');
         ability.update([]);
         Sentry.setUser(null);
         Sentry.setContext('currentContext', null);
         resetStores({ exclude: ['design', 'applicationContext', 'loginOptions'] });

         return true;
      } catch {
         return false;
      }
   };

   getLoggedInUser = async (force = false) => {
      this.contextStore.setLoading(true);
      if (this.userPromise && !force) {
         return this.userPromise;
      }

      this.userPromise = this.httpClient
         .get(`/${this.version}/users/self`)
         .then((resp) => {
            Sentry.setUser({ ...pick(resp.data, ['id', 'email']), username: resp.data?.userName });
            Sentry.setContext('currentContext', pick(resp.data, ['organisationId', 'activeOrganisationId', 'activePeriodId']));

            // Update all stores in one transaction
            applyTransaction(() => {
               this.appendDataToStore(resp.data, this.contextStore as unknown as EntityStore<UsersState>);
               ability.update(resp.data.rules);

               const { sortPref, searchHistory, customFilters } = resp.data?.viewState ?? {};

               uiStore.update(resp.data.viewState);

               store.update({ sortModel: sortPref?.userTable ?? [] });
               searchHistoryStore.update({ entities: searchHistory?.entities ?? [], ids: searchHistory?.ids ?? [] });
               customFiltersStore.update({ entities: customFilters?.entities ?? {}, ids: customFilters?.ids ?? [] });
               orgStore.update({ sortModel: sortPref?.orgTable ?? [] });
               businessActivitiesStore.update({ sortModel: sortPref?.businessActivities ?? [] });
               footprintsStore.update({ sortModel: sortPref?.footprints ?? [] });
               taskStore.update({ sortOrder: sortPref?.taskActions });
               this.contextStore.setLoading(false);
            });

            this.userPromise = undefined;
            return resp.data;
         })
         .catch((error) => {
            if (error?.response?.status === 401) {
               this.hasLoaded = true;
               Sentry.setUser(null);
               Sentry.setContext('currentContext', null);
               this.contextStore.setLoading(false);
               this.userPromise = undefined;
            } else {
               this.setError(error);
               this.contextStore.setLoading(false);
               this.userPromise = undefined;
            }
            return false;
         });

      return this.userPromise;
   };

   getUserById = async (userId: User['id'], options?: { signal?: AbortSignal }) => {
      this.store.setLoading(true);

      return this.httpClient
         .get(`/${this.version}/users/${userId}`, options)
         .then((resp) => {
            this.store.upsert(userId, resp.data);
            this.store.setLoading(false);
            return resp.data;
         })
         .catch((error) => {
            this.setError(error);
            this.store.setLoading(false);
            throw error;
         });
   };

   resetHasLoaded() {
      this.hasLoaded = false;
   }

   isAuthenticated = async () => {
      let storeValue = this.userContextQuery.getValue();

      if (storeValue?.id) {
         return true;
      }
      if (this.hasLoaded) {
         return false;
      }

      const isAuthenticated = await this.getLoggedInUser();

      if (isAuthenticated === false) {
         return false;
      }
      storeValue = this.userContextQuery.getValue();
      return !!storeValue?.id;
   };

   setActiveOrganisation = async (organisationId: Organisation['id'], returnError = false) => {
      const userId = this.userContextQuery.getValue().id;

      this.contextStore.setLoading(true);

      return this.httpClient
         .patch(`/${this.version}/users/${userId}`, {
            activeOrganisationId: organisationId,
         })
         .then((resp) =>
            applyTransaction(() => {
               this.contextStore.update((prevState) => ({
                  ...prevState,
                  ...resp.data,
               }));

               this.contextStore.setLoading(false);
            })
         )
         .catch((error) => {
            this.setError(error, this.contextStore as unknown as EntityStore<UsersState>);
            this.contextStore.setLoading(false);

            if (returnError) {
               throw new Error(error?.response?.data?.message ?? error?.message);
            }
         });
   };

   markNotificationAsRead = async (notificationId: Notification['id']) => {
      const collectionName = 'notifications';

      return this.httpClient
         .post(`/${this.version}/notifications/${notificationId}/markasread`)
         .then(() =>
            this.contextStore.update(({ [collectionName]: collectionName_ }) => ({
               notifications: arrayUpdate(collectionName_, notificationId, { isRead: true }),
            }))
         )
         .catch((error) => {
            this.setError(error, this.contextStore as unknown as EntityStore<UsersState>);
         });
   };

   markAllNotificationsAsRead = async () => {
      const collectionName = 'notifications';
      const userId = this.userContextQuery.getValue().id;

      return this.httpClient
         .post(`/${this.version}/users/${userId}/notifications/readall`)
         .then(() =>
            this.contextStore.update(({ [collectionName]: collectionName_ }) => ({
               notifications: arrayUpdate(collectionName_, () => true, { isRead: true }),
            }))
         )
         .catch((error) => {
            this.setError(error, this.contextStore as unknown as EntityStore<UsersState>);
         });
   };

   deleteNotification = async (notificationId: number) => {
      const collectionName = 'notifications';

      return this.httpClient
         .delete(`/${this.version}/${collectionName}/${notificationId}`)
         .then(() =>
            this.contextStore.update(({ [collectionName]: collectionName_ }) => ({
               [collectionName]: arrayRemove(collectionName_, notificationId),
            }))
         )
         .catch((error) => {
            this.setError(error, this.contextStore as unknown as EntityStore<UsersState>);
         });
   };

   setActivePeriod = async (periodId: Period['id']) => {
      this.contextStore.setLoading(true);

      const userId = this.userContextQuery.getValue().id;

      return this.httpClient
         .patch(`/${this.version}/users/${userId}`, {
            activePeriodId: periodId,
         })
         .then((resp) => {
            this.contextStore.update((prevState) => ({
               ...prevState,
               ...resp.data,
            }));
            return this.contextStore.setLoading(false);
         })
         .catch((error) => {
            this.setError(error, this.contextStore as unknown as EntityStore<UsersState>);
            this.contextStore.setLoading(false);
         });
   };

   setIncludeSubsidiaries = async (includeSubsidiaries: boolean) => {
      this.contextStore.setLoading(true);

      const userId = this.userContextQuery.getValue().id;

      return this.httpClient
         .patch(`/${this.version}/users/${userId}`, {
            includeSubsidiaries,
         })
         .then((resp) => {
            this.contextStore.update((prevState) => ({
               ...prevState,
               ...resp.data,
            }));
            this.contextStore.setLoading(false);

            return true;
         })
         .catch((error) => {
            this.setError(error, this.contextStore as unknown as EntityStore<UsersState>);
            this.contextStore.setLoading(false);

            return false;
         });
   };

   persistViewState = async () => {
      const { id: userId, viewState: existingViewState = {} } = this.userContextQuery.getValue() ?? {};
      if (!userId) return;

      const uiView = pick(uiStore.getValue(), persistableViewStateProperties);

      const currentViewState = {
         ...existingViewState,
         ...uiView,
      };

      const usersState = store.getValue();
      const orgState = orgStore.getValue();
      const baState = businessActivitiesStore.getValue();
      const fpState = footprintsStore.getValue();
      const taskState = taskStore.getValue();
      const shState = searchHistoryStore.getValue();
      const cfState = customFiltersStore.getValue();
      const persistableStoreState = {
         sortPref: {
            userTable: usersState.sortModel,
            orgTable: orgState.sortModel,
            businessActivities: baState.sortModel,
            footprints: fpState.sortModel,
            taskActions: taskState.sortOrder,
         },
         searchHistory: {
            entities: shState.entities,
            ids: shState.ids,
         },
         customFilters: {
            entities: cfState.entities,
            ids: cfState.ids,
         },
      };

      if (currentViewState?.previousOrganisationId) {
         currentViewState.lastVisitedOrganisationId = currentViewState.previousOrganisationId;
      }

      return this.httpClient.patch(`/${this.version}/users/${userId}`, {
         viewState: {
            ...currentViewState,
            ...persistableStoreState,
         },
      });
   };

   setLayout = async (tabName: string, layout: Layout) => {
      if (
         Array.isArray(layout) &&
         layout.length > 0 &&
         !layout.every((layoutItem, index) => layoutItem.w === 1 && layoutItem.h === 1 && layoutItem.x === 0 && layoutItem.y === index)
      ) {
         const { id: userId, layouts: userLayout, activeOrganisationId } = await firstValueFrom(userContextQuery.select());

         if (
            !activeOrganisationId ||
            !userLayout?.[activeOrganisationId] ||
            !(tabName in userLayout[activeOrganisationId]) ||
            layout.some((newLayout) => {
               const previousLayout: LayoutItem | null = activeOrganisationId
                  ? ((userLayout?.[activeOrganisationId]?.[tabName] ?? []).find((prevLayout: LayoutItem) => prevLayout.i === newLayout.i) ?? null)
                  : null;

               return (
                  previousLayout?.x !== newLayout?.x ||
                  previousLayout?.y !== newLayout?.y ||
                  previousLayout?.w !== newLayout?.w ||
                  previousLayout?.h !== newLayout?.h
               );
            })
         ) {
            this.contextStore.setLoading(true);

            const _layout = (layout ?? [])?.map((l) => pick(l, layoutPropsToSave));
            return this.httpClient
               .patch(`/${this.version}/users/${userId}`, {
                  layouts: {
                     ...userLayout,
                     ...(activeOrganisationId
                        ? {
                             [activeOrganisationId]: {
                                ...userLayout?.[activeOrganisationId],
                                [tabName]: Array.from(_layout),
                             },
                          }
                        : {}),
                  },
               })
               .then((resp) => {
                  this.appendDataToStore(resp.data, this.contextStore as unknown as EntityStore<UsersState>);
                  return this.contextStore.setLoading(false);
               })
               .catch((error) => {
                  this.setError(error, this.contextStore as unknown as EntityStore<UsersState>);
                  this.contextStore.setLoading(false);
               });
         }
      }

      return undefined;
   };

   updateUserImage = async (files: File[], userId: User['id']) => {
      const formData = new FormData();
      formData.append('image', files[0]);

      return this.httpClient
         .post(`/${this.version}/users/${userId}/image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
         .then((resp) => {
            if (this.userContextQuery.getValue().id === userId) {
               this.contextStore.update({ groups: this.contextStore.getValue().groups, ...resp.data });
            }
            return this.store.update(userId, resp.data);
         })
         .catch((error) => this.setError(error, this.contextStore as unknown as EntityStore<UsersState>));
   };

   setPageSize = async (pageSize: number) => {
      const userId = this.userContextQuery.getValue().id;

      return this.httpClient
         .patch(`/${this.version}/users/${userId}`, { pageSize })
         .then((resp) => {
            if (this.userContextQuery.getValue().id === userId) {
               this.contextStore.update({ groups: this.contextStore.getValue().groups, ...resp.data });
            }
            return this.store.update(userId, resp.data);
         })
         .catch((error) => this.setError(error, this.contextStore as unknown as EntityStore<UsersState>));
   };

   toggleNotificationTypeKeyActive = async (
      userId: User['id'],
      notificationType: Notification['type'],
      notificationGroupTypeKey: string,
      active: boolean
   ) => {
      await this.updateEntityCollection(userId, 'notificationSettings', notificationGroupTypeKey, { [notificationType]: active });
      void this.updateUserContextIfModified(userId);
   };

   updateNotifications = async (
      userId: User['id'],
      notificationType: Notification['type'],
      notificationValue: any,
      notificationId: Notification['id']
   ) => {
      await this.updateEntityCollection(userId, 'notificationSettings', notificationId, { [notificationType]: notificationValue });
      void this.updateUserContextIfModified(userId);
   };

   updateUserContextIfModified = async (entityId: User['id']) => {
      const userId = this.userContextQuery.getValue().id;
      if (entityId === userId) {
         void this.getLoggedInUser();
      }
   };

   updateEntity = async (entityId: User['id'], changes: Partial<User>) => {
      const updatedUser = await super.updateEntity(entityId, changes);
      void this.updateUserContextIfModified(entityId);
      return updatedUser;
   };

   showTutorial = async () => {
      const userId = this.userContextQuery.getValue().id;
      this.contextStore.update(() => ({ tutorialDone: false }));

      return Promise.resolve(userId ? this.updateEntity(userId, { tutorialDone: false }) : undefined);
   };

   hideTutorial = async () => {
      const userId = this.userContextQuery.getValue().id;
      this.contextStore.update(() => ({ tutorialDone: true, tutorialStep: 0 }));
      return Promise.resolve(userId ? this.updateEntity(userId, { tutorialDone: true }) : undefined);
   };

   setSearchTerm(searchTerm: UsersState['searchTerm']) {
      this.store.update({ searchTerm });
   }

   setSortModel(sortModel: UsersState['sortModel']) {
      this.store.update({ sortModel });
   }

   setShowDeletedUsers(showDeletedUsers: boolean) {
      this.store.update({ showDeletedUsers });
   }

   getPageParams() {
      const dependencies = [];

      dependencies.push(pageSize$);
      dependencies.push(this.query.select('searchTerm').pipe(debounceTime(800)));
      dependencies.push(this.query.select('showDeletedUsers'));
      dependencies.push(this.query.select('sortModel'));

      return combineQueries(dependencies).pipe(
         distinctUntilChanged(isEqual),
         map(
            ([limit, text, showDeleted, sortModel]: [
               ExtractObservableValueType<typeof pageSize$>,
               UsersState['searchTerm'],
               boolean,
               UsersState['sortModel'],
            ]) => {
               const urlParams = new URLSearchParams();

               if (limit) {
                  urlParams.set('limit', String(limit));
               }
               if (showDeleted != undefined) {
                  urlParams.set('deleted', String(showDeleted));
               }
               if (text !== undefined) {
                  if (text?.length > 0) {
                     urlParams.set('text', text);
                     urlParams.set('includeSubsidiaries', String(true));
                  }
                  urlParams.set('organisationId', String(this.userContextQuery.getValue().organisation?.id));
               }

               if (Array.isArray(sortModel) && sortModel.length > 0) {
                  urlParams.set('sort', sortModel.map((item) => `${item.field}:${(item.sort ?? Order.ASC).toLowerCase()}`).join(','));
               }

               return `?${urlParams.toString()}`;
            }
         ),
         distinctUntilChanged(),
         tap((pageQueryParams) => {
            this.pageQueryParams = pageQueryParams;
            if (this.paginate && this?.paginator?.hasPage(1)) {
               this.paginator.clearCache();
               this.paginator.clearCache();
            }
         })
      );
   }

   deleteCredential = async (credentialId: number) => {
      const userId = this.userContextQuery.getValue().id;

      return this.httpClient
         .delete(`/${this.version}/users/${userId}/credentials/${credentialId}`)
         .then(() =>
            this.contextStore.update(({ publicKeys: publicKeys_ }) => ({
               publicKeys: arrayRemove(publicKeys_, (item) => item.credentialId === credentialId),
            }))
         )
         .catch((error) => this.setError(error, this.contextStore as unknown as EntityStore<UsersState>));
   };

   restoreUser = async (userId: User['id']) => {
      return this.httpClient
         .post(`/${this.version}/${this.entityName}/${userId}/restore`)
         .then((resp) => this.store.update(userId, resp.data))
         .catch((error) => this.setError(error));
   };

   getSuggestions = async (key: string | null = null, page = 1, limit = 25, offset?: number) => {
      this.store.setLoading(true);
      const urlParams = new URLSearchParams();

      if (this.useScope === true) {
         urlParams.set('scope', String(true));
      } else if (this.useScope === false) {
         urlParams.set('scope', String(false));
      } else {
         urlParams.set('scope', 'any');
      }
      if (key) {
         urlParams.set('text', key);
      }
      urlParams.set('organisationId', String(this.userContextQuery.getValue().organisation?.id));
      urlParams.set('limit', String(limit ?? this.userContextQuery.getValue().pageSize));
      // a lazy loading grid asks for a row range, which a page number cannot express
      if (typeof offset === 'number') {
         urlParams.set('offset', String(offset));
      } else {
         urlParams.set('page', String(page));
      }
      urlParams.set('includeSubsidiaries', String(true));
      urlParams.set('deleted', String(false));

      const queryString = `?${urlParams.toString()}`;

      return this.httpClient
         .get(`/${this.version}/${this.entityName}${queryString}`)
         .then((resp) => resp.data)
         .catch((error) => this.setError(error))
         .finally(() => this.store.setLoading(false));
   };

   setUserLocale = async (locale: string) => {
      const userId = this.userContextQuery.getValue().id;

      return this.httpClient
         .patch(`/${this.version}/users/${userId}`, { locale })
         .then((resp) => {
            if (this.userContextQuery.getValue().id === userId) {
               this.contextStore.update({ groups: this.contextStore.getValue().groups, ...resp.data });
            }
            return this.store.update(userId, resp.data);
         })
         .catch((error) => this.setError(error, this.contextStore as unknown as EntityStore<UsersState>));
   };

   updateHomeWidgetLayout(newLayout: Layout) {
      this.contextStore.update((prevState) => ({
         ...prevState,
         viewState: {
            ...(prevState.viewState ?? {}),
            home: Array.from(newLayout),
         },
      }));
   }

   saveNote = async (note: string | null) => {
      this.contextStore.update((prevState) => ({
         ...prevState,
         viewState: {
            ...(prevState.viewState ?? {}),
            note,
         },
      }));

      return this.persistViewState();
   };

   updateOrgaTreeExpanded(expandedKeys: string[]) {
      this.contextStore.update((prev) => ({
         ...prev,
         viewState: {
            ...(prev.viewState ?? {}),
            orgaTreeExpanded: expandedKeys,
         },
      }));
   }

   saveOrgaTreeExpanded(expandedKeys: string[]) {
      this.updateOrgaTreeExpanded(expandedKeys);
      return this.persistViewState();
   }

   updateOrganisationsViewOrder(newOrder: number[]) {
      this.contextStore.update((prev) => ({
         ...prev,
         viewState: {
            ...(prev.viewState ?? {}),
            organisationsViewOrder: newOrder,
         },
      }));
   }

   getNotifications = async ({
      page = 1,
      pageSize = 25,
      type,
      referencedModel,
      isRead,
      from,
      to,
      organisationIds,
      sort,
   }: {
      page?: number;
      pageSize?: number;
      type?: string;
      referencedModel?: string;
      isRead?: string;
      from?: string;
      to?: string;
      organisationIds?: string;
      sort?: string;
   } = {}) => {
      const params: Record<string, string> = {
         limit: pageSize.toString(),
         page: page.toString(),
      };

      if (type) params.type = type;
      if (referencedModel) params.referencedModel = referencedModel;
      if (isRead !== undefined) params.isRead = isRead;
      if (from) params.from = from;
      if (to) params.to = to;
      if (organisationIds) params.organisationIds = organisationIds;
      if (sort) {
         params.sort = sort;
      }

      return this.httpClient
         .get(`/${this.version}/users/self/notifications`, { params })
         .then((resp) => resp)
         .catch((error) => {
            this.setError(error);
         });
   };

   async exportAuditLog({ from, to, events }: { from: Date; to: Date; events: AuditLogEventEnum[] }) {
      const searchParams = new URLSearchParams();

      searchParams.set('from', formatISO(from, { representation: 'date' }));
      searchParams.set('to', formatISO(to, { representation: 'date' }));

      events.forEach((event) => searchParams.append('events', event));

      return this.httpClient
         .get(`/${this.version}/logs/export/excel?${searchParams.toString()}`, { responseType: 'blob' })
         .then((resp) => resp)
         .catch((error) => {
            this.setError(error);
            throw error;
         });
   }

   async buildDataSourceQueryParams(params: GridGetRowsParams): Promise<URLSearchParams> {
      if (!this.queryParams) {
         await firstValueFrom(this.queryParamsObservable);
      }

      const urlParams = applyUserListFilters(new URLSearchParams(this.queryParams), params.filterModel);

      if (Array.isArray(params.filterModel?.quickFilterValues) && (params.filterModel?.quickFilterValues?.length ?? 0) > 0) {
         urlParams.set('text', params.filterModel.quickFilterValues.join(' '));
      }

      if (params.sortModel?.length > 0) {
         urlParams.set('sort', params.sortModel.map(({ field, sort }) => `${field}:${sort ?? 'asc'}`).join(','));
      }

      return urlParams;
   }
}
